import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Menu,
} from "@mui/material";
import {
  patchAddAppsWelcomeWizardCampaign,
  reorderAppsWelcomeWizardCampaign,
  deleteAppWelcomeWizardCampaign,
  fetchAppWelcomeWizardStatus,
} from "../../Services/redux/Welcome-wizard/WelcomeWizardActions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function formatDate(ms) {
  const d = new Date(ms);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
     timeZone: "UTC",
  });
}

const countryOptions = ["Countries List", "Germany", "France", "Italy"];
const modelYearOptions = ["Model Year", "2020", "2021", "2022"];
const softwareVersionOptions = ["XXXX", "1.0.0", "2.0.0"];

const getTagValues = (campaignAppData, key) => {
  return campaignAppData?.tags?.find((tag) => tag.key === key)?.values || [];
};

const getSelectedModelsFromInclusions = (
  mmyInclusions,
  portfolioId,
  mmyForPortfolio
) => {
  const modelsSelected = {};

  mmyInclusions.forEach((inclusion) => {
    const parts = inclusion.split("_");

    if (parts.length !== 3) {
      return;
    }

    const [first, model, year] = parts;
    const isValidInclusion =
      first === (portfolioId === "TML" ? "TATA" : portfolioId) ||
      mmyForPortfolio?.[0]?.make === first;

    if (!isValidInclusion) {
      return;
    }

    if (!modelsSelected[model]) {
      modelsSelected[model] = {
        selected: false,
        years: [],
      };
    }

    if (year === "ALL") {
      modelsSelected[model].selected = true;
      const modelData = mmyForPortfolio?.[0]?.models.find((m) => m.name === model);
      if (modelData) {
        modelsSelected[model].years = modelData.years.map((y) => y.toString());
      }
      return;
    }

    modelsSelected[model].years.push(year);
    if (modelsSelected[model].years.length > 0) {
      modelsSelected[model].selected = true;
    }
  });

  return modelsSelected;
};

const getCountrySelection = (countryValues, countries) => {
  if (countryValues.includes("All")) {
    return {
      allSelected: true,
      selectedCountries: countries || [],
    };
  }

  const selectedCountryObjs = countryValues
    .map((iso3Code) => countries?.find((c) => c.iso3Code === iso3Code))
    .filter(Boolean);

  return {
    allSelected: false,
    selectedCountries: selectedCountryObjs,
  };
};

const getSoftwareSelection = (clusterValues, clusterNumberData) => {
  if (clusterValues.includes("All")) {
    return {
      allSelected: true,
      selectedVersions: clusterNumberData || [],
    };
  }

  return {
    allSelected: false,
    selectedVersions: clusterValues,
  };
};

const renderSoftwareVersionItems = (
  isAllSoftwareSelected,
  clusterNumberData,
  selectedSoftwareVersions
) => {
  if (isAllSoftwareSelected) {
    if (clusterNumberData && clusterNumberData.length > 0) {
      return clusterNumberData.map((version) => (
        <ListItem key={version} sx={{ py: 1 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Checkbox edge="start" checked={true} disabled={true} size="small" />
          </ListItemIcon>
          <ListItemText
            primary={version}
            sx={{
              "& .MuiListItemText-primary": {
                fontSize: "14px",
                fontWeight: 500,
              },
            }}
          />
        </ListItem>
      ));
    }

    return (
      <ListItem>
        <ListItemText
          primary="No software versions available"
          sx={{ textAlign: "center", color: "#888" }}
        />
      </ListItem>
    );
  }

  if (selectedSoftwareVersions.length > 0) {
    return selectedSoftwareVersions.map((version) => (
      <ListItem key={version} sx={{ py: 1 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <Checkbox edge="start" checked={true} disabled={true} size="small" />
        </ListItemIcon>
        <ListItemText
          primary={version}
          sx={{
            "& .MuiListItemText-primary": {
              fontSize: "14px",
              fontWeight: 500,
            },
          }}
        />
      </ListItem>
    ));
  }

  return (
    <ListItem>
      <ListItemText
        primary="No software versions selected"
        sx={{ textAlign: "center", color: "#888" }}
      />
    </ListItem>
  );
};

const renderCountryItems = (
  isAllCountriesSelected,
  countries,
  selectedCountries
) => {
  if (isAllCountriesSelected) {
    if (countries && countries.length > 0) {
      return countries
        .toSorted((a, b) => a.name.localeCompare(b.name))
        .map((country) => (
          <ListItem key={country.iso3Code} sx={{ py: 1 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Checkbox edge="start" checked={true} disabled={true} size="small" />
            </ListItemIcon>
            <ListItemText
              primary={country.name}
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "14px",
                  fontWeight: 500,
                },
              }}
            />
          </ListItem>
        ));
    }

    return (
      <ListItem>
        <ListItemText
          primary="No countries available"
          sx={{ textAlign: "center", color: "#888" }}
        />
      </ListItem>
    );
  }

  if (selectedCountries.length > 0) {
    return selectedCountries
      .toSorted((a, b) => a.name.localeCompare(b.name))
      .map((country) => (
        <ListItem key={country.iso3Code} sx={{ py: 1 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Checkbox edge="start" checked={true} disabled={true} size="small" />
          </ListItemIcon>
          <ListItemText
            primary={country.name}
            sx={{
              "& .MuiListItemText-primary": {
                fontSize: "14px",
                fontWeight: 500,
              },
            }}
          />
        </ListItem>
      ));
  }

  return (
    <ListItem>
      <ListItemText
        primary="No countries selected"
        sx={{ textAlign: "center", color: "#888" }}
      />
    </ListItem>
  );
};

// CODE54: SortableAppRow component to handle drag-and-drop functionality for each app row in the table.
// Related items (auto-generated):
// Parents:
//   SDD54MARKETPLACE1099R121: Select the apps display order in the welcome wizard https://github.com/HARMAN-Auto/sp-prod-marketplace-portal-frontend/blob/f8608c4ec483c0d434cc7a600930e6e9384a40d6/docs/SDD/Global-Backend-OemAdmin-LLD.md#L3432
// End related items


function SortableAppRow({ app, rowId, countriesList, onMenuClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: rowId });

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 2,
          backgroundColor: "#fff",
        }
      : {}),
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={rowStyle}
      sx={{ cursor: "grab" }}
    >
      <TableCell sx={{ borderBottom: "0px", width: 44 }}>
        <IconButton
          size="small"
          aria-label="drag app row"
          {...attributes}
          {...listeners}
          sx={{ cursor: "grab" }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      </TableCell>
      <TableCell
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "0px",
        }}
      >
        <img
          src={app.iconUrl}
          alt={app.title}
          style={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            marginRight: 8,
            background: "#fff",
            border: "1px solid #eee",
          }}
        />
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: "15px",
            color: "#222",
          }}
        >
          {app.title}
        </Typography>
      </TableCell>
      <TableCell
        sx={{
          fontWeight: 400,
          fontSize: "14px",
          color: "#5A5B5C",
        }}
      >
        {app.publisherName || "N/A"}
      </TableCell>
      <TableCell
        sx={{
          fontWeight: 400,
          fontSize: "14px",
          color: "#5A5B5C",
        }}
      >
        {countriesList.length || "N/A"}
      </TableCell>
      <TableCell
        sx={{
          fontWeight: 400,
          fontSize: "14px",
          color: "#5A5B5C",
        }}
      >
        {app.version || "N/A"}
      </TableCell>
      <TableCell align="right">
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onMenuClick(e, app);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

SortableAppRow.propTypes = {
  app: PropTypes.object.isRequired,
  rowId: PropTypes.string.isRequired,
  countriesList: PropTypes.array,
  onMenuClick: PropTypes.func.isRequired,
};

export default function ApplicationsListDialog({
  open,
  onClose,
  CampaignAppData,
}) {
  const dispatch = useDispatch();
  const [addAppsDialogOpen, setAddAppsDialogOpen] = useState(false);
  const [searchApp, setSearchApp] = useState("");
  const [selectedAppsToAdd, setSelectedAppsToAdd] = useState([]);
  const [availableApps, setAvailableApps] = useState([]);
  const [countriesList, setCountriesList] = useState(countryOptions);
  const [modelYearDialogOpen, setModelYearDialogOpen] = useState(false);
  const [selectedModels, setSelectedModels] = useState({});
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAppToDelete, setSelectedAppToDelete] = useState(null);
  const [softwareVersionDialogOpen, setSoftwareVersionDialogOpen] =
    useState(false);
  const [selectedSoftwareVersions, setSelectedSoftwareVersions] = useState([]);
  const [isAllSoftwareSelected, setIsAllSoftwareSelected] = useState(false);
  const [countriesDialogOpen, setCountriesDialogOpen] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [isAllCountriesSelected, setIsAllCountriesSelected] = useState(false);
  const [orderedCampaignApps, setOrderedCampaignApps] = useState([]);

  const campaignApps = CampaignAppData?.apps || [];
  const campaignName = CampaignAppData?.name || "";
  const campaignAppOrderKey = campaignApps
    .map((app) => app.packageId || app.id || app.title)
    .join("|");

  useEffect(() => {
    setOrderedCampaignApps(campaignApps);
  }, [campaignAppOrderKey]);

  const menuOpen = Boolean(anchorEl);
  const appsData = useSelector((state) => state.welcomeWizard.appsData);
  const countries = useSelector((state) => state.oemAdminCountires.countries);
  const clusterNumberData = useSelector(
    (state) => state.clusterNumberReducer.clusterNumberData
  );
  const MMYforPortfolio = useSelector(
    (state) => state.makeModelYearReducer.mmyData
  );

  const isDataLoaded =
    CampaignAppData && Object.keys(CampaignAppData).length > 0;

  useEffect(() => {
    const mmyInclusions = getTagValues(CampaignAppData, "mmy_inclusion");
    if (mmyInclusions.length === 0) {
      return;
    }

    const portfolioId = sessionStorage.getItem("portfolioId");
    const allPattern =
      portfolioId === "TML" ? "TATA_ALL_ALL" : `${portfolioId}_ALL_ALL`;
    const hasAllSelected = mmyInclusions.includes(allPattern);
    setIsAllSelected(hasAllSelected);

    if (hasAllSelected) {
      const allModelsSelected = {};
      if (MMYforPortfolio?.length > 0) {
        MMYforPortfolio[0].models.forEach((model) => {
          allModelsSelected[model.name] = {
            selected: true,
            years: model.years.map((year) => year.toString()),
          };
        });
      }
      setSelectedModels(allModelsSelected);
      return;
    }

    setSelectedModels(
      getSelectedModelsFromInclusions(mmyInclusions, portfolioId, MMYforPortfolio)
    );
  }, [CampaignAppData, MMYforPortfolio]);

  // Add useEffect to parse software version selections
  useEffect(() => {
    const clusterValues = getTagValues(CampaignAppData, "clusterNumber");
    if (clusterValues.length === 0) {
      return;
    }

    const softwareSelection = getSoftwareSelection(clusterValues, clusterNumberData);
    setIsAllSoftwareSelected(softwareSelection.allSelected);
    setSelectedSoftwareVersions(softwareSelection.selectedVersions);
  }, [CampaignAppData, clusterNumberData]);

  // Add handlers
  const handleSoftwareVersionClick = () => {
    setSoftwareVersionDialogOpen(true);
  };

  const handleSoftwareVersionDialogClose = () => {
    setSoftwareVersionDialogOpen(false);
  };

  // Get display text for Software Version dropdown
  const getSoftwareVersionDisplayText = () => {
    if (isAllSoftwareSelected) {
      return "All Software Versions";
    }
    const selectedCount = selectedSoftwareVersions.length;
    if (selectedCount === 0) return "Select Software Version";
    return `${selectedCount} Version(s) Selected`;
  };

  const handleModelYearClick = () => {
    setModelYearDialogOpen(true);
  };

  const handleModelYearDialogClose = () => {
    setModelYearDialogOpen(false);
  };

  // Get display text for Model Year dropdown
  const getModelYearDisplayText = () => {
    if (isAllSelected) {
      return "All Models & Years";
    }
    const selectedCount = Object.keys(selectedModels).length;
    if (selectedCount === 0) return "Select Model Year";
    return `${selectedCount} Model(s) Selected`;
  };

  useEffect(() => {
    console.log("CampaignAppData updated:", CampaignAppData);
    console.log("appsData from Redux:", appsData);

    if (CampaignAppData && Object.keys(CampaignAppData).length > 0) {
      const tags = [...CampaignAppData.tags];
      if (tags && tags.length > 0) {
        tags[0].key = "ecuType_inclusion";
        setCountriesList(() => [
          ...(tags.find((tag) => tag.key === "country")?.values || []),
        ]);
        const payload = {
          tags: [...tags],
        };
        console.log("Fetching apps with payload:", payload);
        dispatch(fetchAppWelcomeWizardStatus(payload));
      }
    }
  }, [CampaignAppData, dispatch]);

  useEffect(() => {
    console.log("appsData updated in useEffect:", appsData);
    if (appsData && appsData.length > 0) {
      setAvailableApps(appsData);
    }
  }, [appsData]);

  useEffect(() => {
    console.log("Countries Data:", countries);
    console.log("Cluster Number Data:", clusterNumberData);
    console.log("MMY for Portfolio Data:", MMYforPortfolio);
  }, [countries, clusterNumberData, MMYforPortfolio]);

  const handleAddAppsClick = () => {
    setAddAppsDialogOpen(true);
    setSelectedAppsToAdd([]);
    setSearchApp("");
  };

  const handleAddAppsDialogClose = () => {
    setAddAppsDialogOpen(false);
    setSelectedAppsToAdd([]);
    setSearchApp("");
  };

  const handleAppToggle = (app) => {
    const existingAppsCount = campaignApps.length;
    const maxNewAppsAllowed = 8 - existingAppsCount;

    if (selectedAppsToAdd.includes(app.packageId)) {
      setSelectedAppsToAdd(
        selectedAppsToAdd.filter((a) => a !== app.packageId)
      );
    } else if (selectedAppsToAdd.length < maxNewAppsAllowed) {
      setSelectedAppsToAdd([...selectedAppsToAdd, app.packageId]);
    }
  };

  const closeTheApplicationListDialog = () => {
    onClose();
  };

  const handleSaveAddedApps = () => {
    console.log("Selected apps to add:", selectedAppsToAdd);
    console.log("To Campaign:", campaignApps);
    const payload = {
      apps: selectedAppsToAdd,
    };
    dispatch(patchAddAppsWelcomeWizardCampaign(campaignName, payload));
    console.log("Payload to update campaign with new apps:", selectedAppsToAdd);
    setAddAppsDialogOpen(false);
  };

  const handleMenuClick = (event, app) => {
    setAnchorEl(event.currentTarget);
    setSelectedAppToDelete(app);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAppToDelete(null);
  };

  const handleDeleteApp = () => {
    console.log("Deleting app:", selectedAppToDelete);
    dispatch(
      deleteAppWelcomeWizardCampaign(
        campaignName,
        selectedAppToDelete.packageId
      )
    );
    handleMenuClose();
  };

  const getRowId = (app, idx) => app.packageId || `row-${idx}`;

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }

    setOrderedCampaignApps((prev) => {
      const oldIndex = prev.findIndex((app, idx) => getRowId(app, idx) === active.id);
      const newIndex = prev.findIndex((app, idx) => getRowId(app, idx) === over.id);

      if (oldIndex < 0 || newIndex < 0) {
        return prev;
      }

      const reordered = arrayMove(prev, oldIndex, newIndex);
      const orderedPackageIds = reordered.map((app) => app.packageId).filter(Boolean);
      dispatch(
        reorderAppsWelcomeWizardCampaign(campaignName, {
          apps: orderedPackageIds,
        })
      );

      return reordered;
    });
  };

 const filteredAvailableApps = availableApps
  .filter((app) => {
    // Exclude apps that are already in the campaign
    const isAlreadyInCampaign = campaignApps.some(
      (campaignApp) => campaignApp.packageId === app.packageId
    );
    return !isAlreadyInCampaign;
  })
  .filter((app) => {
    // Filter by search text
    return app.title.toLowerCase().includes(searchApp.toLowerCase());
  });

  useEffect(() => {
    const countryValues = getTagValues(CampaignAppData, "country");
    if (countryValues.length === 0) {
      return;
    }

    const countrySelection = getCountrySelection(countryValues, countries);
    setIsAllCountriesSelected(countrySelection.allSelected);
    setSelectedCountries(countrySelection.selectedCountries);
  }, [CampaignAppData, countries]);

  // Add handlers
  const handleCountriesClick = () => {
    setCountriesDialogOpen(true);
  };

  const handleCountriesDialogClose = () => {
    setCountriesDialogOpen(false);
  };

  // Get display text for Countries dropdown
  const getCountriesDisplayText = () => {
    if (isAllCountriesSelected) {
      return "All Countries";
    }
    const selectedCount = selectedCountries.length;
    if (selectedCount === 0) return "Select Countries";
    return `${selectedCount} Countr${selectedCount > 1 ? "ies" : "y"} Selected`;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={closeTheApplicationListDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
            fontSize: "22px",
            pb: 0,
          }}
        >
          <Box sx={{display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center" }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "22px",
              color: "#5A5B5C",
              mb: 2,
            }}
          >
            Applications List
          </Typography>
           <Typography 
            sx={{
                   fontWeight: "400 !important",
              fontSize: "16px !important",
              color: "#5A5B5C !important",
              mb: 2,
            }}> ({campaignName})</Typography>
            </Box>
          
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleAddAppsClick}
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                borderRadius: "6px",
                background: "#0070f3",
                color: "#fff",
                px: 3,
                boxShadow: "none",
                textTransform: "none",
              }}
              disabled={campaignApps.length >= 8}
            >
              Add Apps
            </Button>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {isDataLoaded ? (
            <>
              <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    flexWrap: "nowrap",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#5A5B5C",
                      }}
                    >
                      Campaign Name
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "16px",
                        color: "#2A2C2D",
                      }}
                    >
                      {campaignName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#5A5B5C",
                      }}
                    >
                      No. of Apps
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "16px",
                        color: "#2A2C2D",
                      }}
                    >
                      {campaignApps.length}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#5A5B5C",
                      }}
                    >
                      Campaign Duration
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "16px",
                        color: "#2A2C2D",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatDate(CampaignAppData.startDate)}{" "}
                      {CampaignAppData.endDate
                        ? "- " + formatDate(CampaignAppData.endDate)
                        : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 180, maxWidth: 260 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#5A5B5C",
                        mb: 0.5,
                      }}
                    >
                      Countries
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "nowrap",
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "16px",
                          color: "#2A2C2D",
                          mb: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Countries List
                      </Typography>
                      <Select
                        value=""
                        renderValue={() => getCountriesDisplayText()}
                        size="small"
                        displayEmpty
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: "14px",
                          "& .MuiSelect-select": {
                            py: 0.5,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            cursor: "pointer",
                          },
                        }}
                        onOpen={(e) => {
                          e.preventDefault();
                          handleCountriesClick();
                        }}
                        open={false}
                      >
                        {/* Empty - no menu items needed */}
                      </Select>
                    </Box>
                  </Box>

                  {/* Model Year Select */}

                  <Box sx={{ minWidth: 180, maxWidth: 260 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#5A5B5C",
                        mb: 0.5,
                      }}
                    >
                      Model Year
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "nowrap",
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "16px",
                          color: "#2A2C2D",
                          mb: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Model Year
                      </Typography>
                      <Select
                        value=""
                        renderValue={() => getModelYearDisplayText()}
                        size="small"
                        displayEmpty
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: "14px",
                          "& .MuiSelect-select": {
                            py: 0.5,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            cursor: "pointer",
                          },
                        }}
                        onOpen={(e) => {
                          e.preventDefault();
                          handleModelYearClick();
                        }}
                        open={false}
                      >
                        {/* Empty - no menu items needed */}
                      </Select>
                    </Box>
                  </Box>

                  {/* Software Version Select */}
                  {sessionStorage.getItem("idpLogin") === "true" &&
                  <Box sx={{ minWidth: 200, maxWidth: 300 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#5A5B5C",
                        mb: 0.5,
                      }}
                    >
                      Software Version
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "nowrap",
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "16px",
                          color: "#2A2C2D",
                          mb: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Software Version
                      </Typography>
                      <Select
                        value=""
                        renderValue={() => getSoftwareVersionDisplayText()}
                        size="small"
                        displayEmpty
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: "14px",
                          "& .MuiSelect-select": {
                            py: 0.5,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            cursor: "pointer",
                          },
                        }}
                        onOpen={(e) => {
                          e.preventDefault();
                          handleSoftwareVersionClick();
                        }}
                        open={false}
                      >
                        {/* Empty - no menu items needed */}
                      </Select>
                    </Box>
                  </Box>
                }
                </Box>
              </Paper>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead sx={{ background: "#F5F5F5" }}>
                    <TableRow>
                      <TableCell sx={{ width: 44, fontWeight: 600, fontSize: "14px" }}>
                        <DragIndicatorIcon fontSize="small" sx={{ color: "#5A5B5C" }} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
                        Application Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
                        Publisher
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
                        Countries
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>
                        App Version
                      </TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext
                      items={orderedCampaignApps.map((app, idx) => getRowId(app, idx))}
                      strategy={verticalListSortingStrategy}
                    >
                      <TableBody>
                    {orderedCampaignApps.length > 0 ? (
                      orderedCampaignApps.map((app, idx) => (
                        <SortableAppRow
                          key={getRowId(app, idx)}
                          app={app}
                          rowId={getRowId(app, idx)}
                          countriesList={countriesList}
                          onMenuClick={handleMenuClick}
                        />
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No Apps Available
                        </TableCell>
                      </TableRow>
                    )}
                      </TableBody>
                    </SortableContext>
                  </DndContext>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300,
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem disabled={campaignApps.length === 1} onClick={handleDeleteApp}>
          <ListItemIcon>
            <DeleteIcon
              style={{
                cursor: campaignApps.length === 1 ? "not-allowed" : "pointer",
              }}
              
              fontSize="small"
              sx={{ color: "#d32f2f" }}
            />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            sx={{ color: "#d32f2f", fontWeight: 500 }}
          />
        </MenuItem>
      </Menu>

      {/* Add Apps Dialog */}
      {addAppsDialogOpen && (
        <Dialog
          open={addAppsDialogOpen}
          onClose={handleAddAppsDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: 600,
              fontSize: "18px",
              pb: 1,
            }}
          >
            Set App for Welcome Wizard
            <IconButton onClick={handleAddAppsDialogClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  mb: 1,
                  fontSize: "14px",
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  color: "#2A2C2D",
                }}
              >
                Campaign Name
              </Typography>
              <TextField
                fullWidth
                disabled
                value={CampaignAppData?.name || ""}
                sx={{
                  mb: 2,
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#5A5B5C",
                  },
                }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  mb: 1,
                  fontSize: "14px",
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  color: "#2A2C2D",
                }}
              >
                Apps (max {8 - (CampaignAppData.apps?.length || 0)} apps can be
                added)
              </Typography>
              <TextField
                fullWidth
                placeholder="Search for App"
                value={searchApp}
                onChange={(e) => setSearchApp(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Box
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                  maxHeight: 300,
                  overflowY: "auto",
                  background: "#fafafa",
                }}
              >
                {filteredAvailableApps.length > 0 ? (
                  <List dense>
                    {filteredAvailableApps.map((app) => (
                      <ListItem
                        key={app.packageId}
                        button
                        onClick={() => handleAppToggle(app)}
                        sx={{ py: 0.5 }}
                        disabled={
                          !selectedAppsToAdd.includes(app.packageId) &&
                          selectedAppsToAdd.length >=
                            8 - campaignApps.length
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Checkbox
                            edge="start"
                            checked={selectedAppsToAdd.includes(app.packageId)}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText primary={app.title} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography sx={{ color: "#888", fontSize: 14 }}>
                      {availableApps.length === 0
                        ? "Loading apps..."
                        : "No apps found"}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "flex-end", pb: 2, px: 3 }}>
            <Button
              onClick={handleAddAppsDialogClose}
              variant="outlined"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: 14,
                background: "#f5f5f5",
                color: "#222",
                borderRadius: "6px",
                textTransform: "none",
                px: 4,
                border: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAddedApps}
              variant="contained"
              sx={{
                ml: 2,
                background: "#0070f3",
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: 14,
                borderRadius: "6px",
                textTransform: "none",
                px: 4,
              }}
              disabled={selectedAppsToAdd.length === 0}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {modelYearDialogOpen && (
        <Dialog
          open={modelYearDialogOpen}
          onClose={handleModelYearDialogClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: 600,
              fontSize: "18px",
              pb: 1,
            }}
          >
            Model Year Selection
            <IconButton onClick={handleModelYearDialogClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              {/* Model Column */}
              <Box sx={{ flex: 1, borderRight: "1px solid #E0E0E0", pr: 2 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "16px",
                    mb: 2,
                    color: "#2A2C2D",
                  }}
                >
                  Model
                </Typography>
                <Box
                  sx={{
                    maxHeight: 400,
                    overflowY: "auto",
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px",
                    background: "#fafafa",
                  }}
                >
                  <List dense>
                    {/* Show models that have selections or if All is selected */}
                    {MMYforPortfolio?.[0]?.models.map((model) => {
                      const hasSelection =
                        selectedModels[model.name]?.selected ||
                        selectedModels[model.name]?.years?.length > 0;
                      // Only show models that are selected or if "All" is selected
                      if (!hasSelection && !isAllSelected) {
                        return null;
                      }

                      return (
                        <ListItem key={model.name} sx={{ py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Checkbox
                              edge="start"
                              checked={true}
                              disabled={true}
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={model.name}
                            sx={{
                              "& .MuiListItemText-primary": {
                                fontSize: "14px",
                                fontWeight: 500,
                              },
                            }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              </Box>

              {/* Year Column - Show only selected models and their selected years */}
              <Box sx={{ flex: 1, pl: 2 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "16px",
                    mb: 2,
                    color: "#2A2C2D",
                  }}
                >
                  Year
                </Typography>
                <Box sx={{ mb: 2, height: 40 }} /> {/* Spacer for alignment */}
                <Box
                  sx={{
                    maxHeight: 400,
                    overflowY: "auto",
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px",
                    background: "#fafafa",
                    mt:"-55px !important"
                  }}
                >
                  {/* Show models that have selections */}
                  {MMYforPortfolio?.[0]?.models.map((model) => {
                    const selectedYears =
                      selectedModels[model.name]?.years || [];

                    // Only show years section if this model has selected years OR if "All" is selected
                    if (selectedYears.length === 0 && !isAllSelected) {
                      return null;
                    }

                    return (
                      <Box key={model.name} sx={{ mb: 2 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#0070f3",
                            px: 2,
                            py: 1,
                            background: "#f5f5f5",
                          }}
                        >
                          {model.name}
                        </Typography>
                        <List dense>
                          {/* Show only selected years */}
                          {model.years.map((year) => {
                            const isYearSelected =
                              selectedYears.includes(year.toString()) ||
                              isAllSelected;

                            // Only render if year is selected
                            if (!isYearSelected) return null;

                            return (
                              <ListItem
                                key={`${model.name}-${year}`}
                                sx={{ py: 1, px: 2 }}
                              >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <Checkbox
                                    edge="start"
                                    checked={true}
                                    disabled={true}
                                    size="small"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={year}
                                  sx={{
                                    "& .MuiListItemText-primary": {
                                      fontSize: "14px",
                                    },
                                  }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "flex-end", pb: 2, px: 3 }}>
            <Button
              onClick={handleModelYearDialogClose}
              variant="outlined"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: 14,
                background: "#f5f5f5",
                color: "#222",
                borderRadius: "6px",
                textTransform: "none",
                px: 4,
                border: "none",
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {softwareVersionDialogOpen && (
        <Dialog
          open={softwareVersionDialogOpen}
          onClose={handleSoftwareVersionDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: 600,
              fontSize: "18px",
              pb: 1,
            }}
          >
            Software Version Selection
            <IconButton onClick={handleSoftwareVersionDialogClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  mb: 2,
                  color: "#2A2C2D",
                }}
              >
                Software Versions
              </Typography>
              <Box
                sx={{
                  maxHeight: 400,
                  overflowY: "auto",
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                  background: "#fafafa",
                }}
              >
                <List dense>
                  {renderSoftwareVersionItems(
                    isAllSoftwareSelected,
                    clusterNumberData,
                    selectedSoftwareVersions
                  )}
                </List>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "flex-end", pb: 2, px: 3 }}>
            <Button
              onClick={handleSoftwareVersionDialogClose}
              variant="outlined"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: 14,
                background: "#f5f5f5",
                color: "#222",
                borderRadius: "6px",
                textTransform: "none",
                px: 4,
                border: "none",
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {countriesDialogOpen && (
        <Dialog
          open={countriesDialogOpen}
          onClose={handleCountriesDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: 600,
              fontSize: "18px",
              pb: 1,
            }}
          >
            Countries Selection
            <IconButton onClick={handleCountriesDialogClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  mb: 2,
                  color: "#2A2C2D",
                }}
              >
                Countries
              </Typography>
              <Box
                sx={{
                  maxHeight: 400,
                  overflowY: "auto",
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                  background: "#fafafa",
                }}
              >
                <List dense>
                  {renderCountryItems(
                    isAllCountriesSelected,
                    countries,
                    selectedCountries
                  )}
                </List>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "flex-end", pb: 2, px: 3 }}>
            <Button
              onClick={handleCountriesDialogClose}
              variant="outlined"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: 14,
                background: "#f5f5f5",
                color: "#222",
                borderRadius: "6px",
                textTransform: "none",
                px: 4,
                border: "none",
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

ApplicationsListDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  CampaignAppData: PropTypes.shape({
    name: PropTypes.string,
    apps: PropTypes.arrayOf(
      PropTypes.shape({
        packageId: PropTypes.string,
        title: PropTypes.string,
        iconUrl: PropTypes.string,
        publisherName: PropTypes.string,
        version: PropTypes.string,
      })
    ),
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        values: PropTypes.arrayOf(PropTypes.string),
      })
    ),
    startDate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    endDate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};
